import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { insightsAtom, insightsLoadingAtom } from '../atoms/insightsAtom';
import {
  DashboardInsights,
  OptInRateInsights,
  AdoptionsInsights,
  InsightsQuery,
} from '../types/insights';
import api from '../services/api';

/**
 * Custom hook to read the insights state.
 * Returns the current insights data or null if not loaded/error.
 */
export const useInsightsState = () => {
  const insightsData = useAtomValue(insightsAtom);
  const loadingState = useAtomValue(insightsLoadingAtom);

  return {
    insights: insightsData,
    ...loadingState,
  };
};

/**
 * Custom hook to provide actions related to insights.
 * Provides functions to fetch different types of insights from the backend API.
 */
export const useInsightsActions = () => {
  const setInsights = useSetAtom(insightsAtom);
  const setLoadingState = useSetAtom(insightsLoadingAtom);

  // Helper function to set loading state
  const setLoading = useCallback(
    (isLoading: boolean, error: string | null = null, isRefreshing: boolean = false) => {
      setLoadingState({ isLoading, isRefreshing, error });
    },
    [setLoadingState]
  );

  // Helper function to add minimum loading time (prevent flickering)
  const withMinimumLoadingTime = useCallback(
    async <T>(
      asyncOperation: () => Promise<T>,
      isRefresh: boolean = false,
      minTime: number = 800
    ): Promise<T> => {
      const startTime = Date.now();
      setLoading(true, null, isRefresh);

      try {
        const result = await asyncOperation();
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minTime - elapsedTime);

        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        setLoading(false);
        return result;
      } catch (error) {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minTime - elapsedTime);

        if (remainingTime > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingTime));
        }

        throw error;
      }
    },
    [setLoading]
  );

  // Function to fetch comprehensive dashboard insights
  const getDashboardInsights = useCallback(
    async (query?: InsightsQuery, isRefresh: boolean = false) => {
      try {
        await withMinimumLoadingTime(async () => {
          const params = new URLSearchParams();
          if (query?.period) params.append('period', query.period);
          if (query?.startDate) params.append('startDate', query.startDate);
          if (query?.endDate) params.append('endDate', query.endDate);
          if (query?.selectedYear) params.append('selectedYear', query.selectedYear.toString());
          if (query?.selectedQuarter) params.append('selectedQuarter', query.selectedQuarter);
          if (query?.selectedMonth) params.append('selectedMonth', query.selectedMonth);

          const response = await api.get<DashboardInsights>(`/insights/dashboard?${params}`);
          setInsights(response.data);
        }, isRefresh);
      } catch (error) {
        console.error('Failed to fetch dashboard insights:', error);
        setLoading(false, error instanceof Error ? error.message : 'Failed to fetch insights');
      }
    },
    [setInsights, withMinimumLoadingTime, setLoading]
  );

  // Function to fetch opt-in rate insights only
  const getOptInRateInsights = useCallback(
    async (query?: InsightsQuery) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query?.period) params.append('period', query.period);
        if (query?.startDate) params.append('startDate', query.startDate);
        if (query?.endDate) params.append('endDate', query.endDate);
        if (query?.selectedYear) params.append('selectedYear', query.selectedYear.toString());
        if (query?.selectedQuarter) params.append('selectedQuarter', query.selectedQuarter);
        if (query?.selectedMonth) params.append('selectedMonth', query.selectedMonth);

        const response = await api.get<OptInRateInsights>(`/insights/opt-in-rate?${params}`);
        setLoading(false);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch opt-in rate insights:', error);
        setLoading(
          false,
          error instanceof Error ? error.message : 'Failed to fetch opt-in rate insights'
        );
        return null;
      }
    },
    [setLoading]
  );

  // Function to fetch adoptions insights only
  const getAdoptionsInsights = useCallback(
    async (query?: InsightsQuery) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (query?.period) params.append('period', query.period);
        if (query?.startDate) params.append('startDate', query.startDate);
        if (query?.endDate) params.append('endDate', query.endDate);
        if (query?.selectedYear) params.append('selectedYear', query.selectedYear.toString());
        if (query?.selectedQuarter) params.append('selectedQuarter', query.selectedQuarter);
        if (query?.selectedMonth) params.append('selectedMonth', query.selectedMonth);

        const response = await api.get<AdoptionsInsights>(`/insights/adoptions?${params}`);
        setLoading(false);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch adoptions insights:', error);
        setLoading(
          false,
          error instanceof Error ? error.message : 'Failed to fetch adoptions insights'
        );
        return null;
      }
    },
    [setLoading]
  );

  return {
    getDashboardInsights,
    getOptInRateInsights,
    getAdoptionsInsights,
  };
};
